import { ItineraryFilter } from "@eventr/common";
import { useMemo } from "react";
import { useBoundStore } from "state/bound.store";
import { useShallow } from "zustand/react/shallow";
import { getEntryTimestamp, toMs } from "app/activity-feed/activity-feed-utils";

export function useActivityFeed() {
  const { selectedItinerary, firstColdStartAt, activityFeedReadUpToAt, getCurrentUser } = useBoundStore(
    useShallow((s) => ({
      selectedItinerary: s.selectedItinerary,
      firstColdStartAt: s.firstColdStartAt,
      activityFeedReadUpToAt: s.activityFeedReadUpToAt,
      getCurrentUser: s.getCurrentUser,
    })),
  );

  const cutoff = Math.max(firstColdStartAt ?? 0, activityFeedReadUpToAt ?? 0);

  return useMemo(() => {
    if (!selectedItinerary?.v2) return [];

    const iFilter = new ItineraryFilter(selectedItinerary.v2, {
      removeDuplicateEntries: true,
      userVisibility: { selectedUser: getCurrentUser() },
    });

    const days = iFilter.groupBy("date", {
      dateAccessor: (entry) => {
        if ("updated_at" in entry.component && typeof entry.component.updated_at === "number") {
          return toMs(entry.component.updated_at);
        }
        if ("created_at" in entry.component && typeof entry.component.created_at === "number") {
          return toMs(entry.component.created_at);
        }
      },
      sortBy: "desc",
    });

    return days
      .map((day) => ({
        ...day,
        data: day.data
          .filter(({ component }) => {
            const createdAt =
              "created_at" in component && typeof component.created_at === "number" ? toMs(component.created_at) : 0;

            const updatedAt =
              "updated_at" in component && typeof component.updated_at === "number" ? toMs(component.updated_at) : 0;

            const latestChange = Math.max(createdAt, updatedAt);

            return latestChange > cutoff;
          })
          .sort(({ component: c1 }, { component: c2 }) => getEntryTimestamp(c2) - getEntryTimestamp(c1)),
      }))
      .filter((day) => day.data.length > 0);
  }, [cutoff, getCurrentUser, selectedItinerary?.v2]);
}
